import {autorun, reaction} from "mobx";
import {AppConfiguration} from "../core/config/app-configuration";
import {ThematicInspectorConfiguration} from "../config/thematic-inspector-configuration";
import esriRequest from "@arcgis/core/request";
import * as esriSymbolUtils from "@arcgis/core/symbols/support/symbolUtils";
import _ from "lodash";

import {
    Grundstueck,
    Bodenbedeckung,
    Grundbuchkreis,
    Nachfuehrungskreis,
    Zone,
    Bauamt,
    Gemeindekanzlei
} from "./grundstueck";
import {Projekt} from "./projekt";
import {Gebaeude, Eingang, Wohnung} from "./gebaeude";
import {MapState} from "../core/map-state";
import {PathUtils} from "../utils/path-utils";
import {FormatUtils} from "../utils/format-utils";
import {ObjektluStore} from "../stores/objektlu-store-mobx";

export class Objektlu {
    config: AppConfiguration;
    tiConfig: ThematicInspectorConfiguration;
    mapStore: MapState;
    layerlist: HashMap<HashMap<string|number>>;
    oStore: ObjektluStore;
    constructor(config: AppConfiguration, tiConfig: ThematicInspectorConfiguration, mapStore: MapState,
                layerlist: HashMap<HashMap<string|number>>, oStore: ObjektluStore) {
        this.config = config;
        this.tiConfig = tiConfig;
        this.mapStore = mapStore;
        this.layerlist = layerlist;
        this.oStore = oStore;
    }
    loadGrundstueck(egrid: string): Promise<string | null> {
        return new Promise((resolve) => {
            this.oStore.loading = true;
            this.oStore.status = "loading";
            const requestOptions = {} as __esri.RequestOptions;
            const service = this.tiConfig;
            requestOptions.responseType = service.xhrOptions.responseType;
            if (service.xhrOptions.usePost) {
                requestOptions.method = "post";
            }
            if (service.xhrOptions.timeout) {
                requestOptions.timeout = service.xhrOptions.timeout;
            }
            const requestData = {
                type: "objektlu",
                perimeter: {
                    selection: {
                        layerName: "AVLSKOMB_DS",
                        filter: " EGRIS_EGRID IN ('" +
                            FormatUtils.encodeForSQL(egrid) + "') AND QUELLE_LS_SR IN (0,2)"     }
                }
            };
            requestOptions.query = {
                job: JSON.stringify(requestData),
                f: "pjson"
            };
            esriRequest(service.url, requestOptions).then((response: __esri.RequestResponse) => {
                if (response.data){
                    const grundstueck = new Grundstueck();
                    const feature = response.data.features[0];
                    grundstueck.egrid = feature.attributes.EGRIS_EGRID;
                    grundstueck.gbperCode = parseInt(feature.attributes.GBPER_CODE, 10);
                    grundstueck.gbperName = feature.attributes.GBPER_NAME;
                    grundstueck.nummer = parseInt(feature.attributes.NUMMER, 10);
                    grundstueck.art =
                        this.prepareGrundstueckArt(grundstueck,
                            response.data.operationResults.AVLSKOMB_DS.features);
                    grundstueck.artCode = parseInt(feature.attributes.ART, 10);
                    grundstueck.flaeche = parseFloat(feature.attributes.FLAECHENMASS);
                    grundstueck.attributes = feature.attributes;
                    grundstueck.geometry = this.addGeometryType(feature.geometry);
                    grundstueck.data = this.removeGeometry(response.data);
                    grundstueck.nachfuehrungskreis =
                        this.prepareNachfuehrungskreis(grundstueck,
                            response.data.operationResults.AVNFKXXX_DS.features);
                    grundstueck.grundbuchkreis =
                        this.prepareGrundbuchkreis(grundstueck, response.data.operationResults.GRZGBKRX_DS.features);
                    grundstueck.bauamt =
                        this.prepareBauamt(grundstueck,
                            response.data.operationResults.GRZKOBAU_DS.features);
                    grundstueck.gemeindekanzlei =
                        this.prepareGemeindekanzlei(grundstueck,
                            response.data.operationResults.GEMINFOX_DS.features);
                    grundstueck.zonen =
                        this.prepareZonen(grundstueck, response.data.operationResults.ZPGNDNTZ_DS.features);
                    grundstueck.bodenbedeckung =
                        this.prepareBoden(grundstueck, response.data.operationResults.AVGSBBBX_DS.features);
                    grundstueck.gebaeude =
                        this.prepareGebaeude(
                            response.data.operationResults.KGWRGPUB_DS.features,
                            response.data.operationResults.KGWRWPUB_DS.features,
                            grundstueck,
                            response.data.operationResults.AVEINXXX_DS.features,
                            response.data.operationResults.AVGSBBBX_DS.features);
                    const projekte: Projekt[] = [];
                    response.data.operationResults.BAGEDOSS_DS.features.forEach((pro: any) => {
                        // Baugesuche mit unerwünschtem Verfahrenstyp überspringen
                        if (service.hideProjekte.includes(pro.attributes.VERFHRTYP)) {
                            return;
                        }
                        const projekt = new Projekt();
                        projekt.bagenr = pro.attributes.BAGENR;
                        pro.attributes.LIST_PRZNUMMERN.split(",").forEach(
                            (nr: string) => {
                                projekt.prznummern.push(parseInt(nr.trim(), 10));
                            }
                        );
                        projekt.gegenstand = pro.attributes.GEGENSTAND;
                        projekt.status = pro.attributes.STATUS;
                        projekt.attributes = pro.attributes;
                        projekt.geometry = this.addGeometryType(pro.geometry);
                        projekte.push(projekt);
                    });
                    grundstueck.projekte = projekte;
                    this.oStore.addGrundstueck(grundstueck);
                }
                this.oStore.loading = false;
                this.oStore.status = "successful";
                resolve(egrid);
            }).catch((error) => {
                console.error(error);
                this.oStore.loading = false;
                this.oStore.status = "error";
                this.oStore.statusText = service.errorMessage;
                if (error.details.httpStatus === 400){
                    this.oStore.status = "not-found";
                    this.oStore.statusText = service.notFoundMessage.replace("{objektArt}","Grundstück");
                    this.oStore.statusText = this.oStore.statusText.replace("{objektNr}", egrid);
                }
                resolve(null);
            });
        });
    }
    loadGrundstuecke(gbperCode: number, nummern: number[]): Promise<number[]> {
        return new Promise((resolve) => {
            // check cache
            const newNummern = nummern.filter((nummer) => (!this.oStore.grundstueckMap.has(gbperCode + "-" + nummer)));
            if (newNummern.length > 0) {
                this.oStore.loading = true;
                this.oStore.status = "loading";
                const promises: Array<Promise<number>> = [];
                const requestOptions = {} as __esri.RequestOptions;
                const service = this.tiConfig;
                requestOptions.responseType = service.xhrOptions.responseType;
                if (service.xhrOptions.usePost) {
                    requestOptions.method = "post";
                }
                if (service.xhrOptions.timeout) {
                    requestOptions.timeout = service.xhrOptions.timeout;
                }
                // Es können leider nicht mehrere Grundstücke auf einmal abgefragt werden, da sonst die Bodenbedeckung
                // für alle Grundstücke zusammen zurückgeliefert wird und nicht pro Grundstück.
                newNummern.forEach((nummer: number)=>{
                    promises.push(new Promise((subResolve) => {
                        const requestData = {
                            type: "objektlu",
                            perimeter: {
                                selection: {
                                    layerName: "AVLSKOMB_DS",
                                    filter: " GBPER_CODE = " + gbperCode
                                        + " AND NUMMER IN ('" + nummer + "') AND QUELLE_LS_SR IN (0,2)"
                                }
                            }
                        };
                        requestOptions.query = {
                            job: JSON.stringify(requestData),
                            f: "pjson"
                        };
                        esriRequest(service.url, requestOptions).then((response: __esri.RequestResponse) => {
                            if (response.data) {
                                response.data.features.forEach((feature: any) => {
                                    const grundstueck = new Grundstueck();
                                    grundstueck.egrid = feature.attributes.EGRIS_EGRID;
                                    grundstueck.gbperCode = parseInt(feature.attributes.GBPER_CODE, 10);
                                    grundstueck.gbperName = feature.attributes.GBPER_NAME;
                                    grundstueck.nummer = parseInt(feature.attributes.NUMMER, 10);
                                    grundstueck.art =
                                        this.prepareGrundstueckArt(grundstueck,
                                            response.data.operationResults.AVLSKOMB_DS.features);
                                    grundstueck.artCode = parseInt(feature.attributes.ART, 10);
                                    grundstueck.flaeche = parseFloat(feature.attributes.FLAECHENMASS);
                                    grundstueck.attributes = feature.attributes;
                                    grundstueck.geometry = this.addGeometryType(feature.geometry);
                                    grundstueck.data = this.removeGeometry(response.data);
                                    grundstueck.nachfuehrungskreis =
                                        this.prepareNachfuehrungskreis(grundstueck,
                                            response.data.operationResults.AVNFKXXX_DS.features);
                                    grundstueck.grundbuchkreis =
                                        this.prepareGrundbuchkreis(grundstueck,
                                            response.data.operationResults.GRZGBKRX_DS.features);
                                    grundstueck.bauamt =
                                        this.prepareBauamt(grundstueck,
                                            response.data.operationResults.GRZKOBAU_DS.features);
                                    grundstueck.gemeindekanzlei =
                                        this.prepareGemeindekanzlei(grundstueck,
                                            response.data.operationResults.GEMINFOX_DS.features);
                                    grundstueck.zonen = this.prepareZonen(grundstueck,
                                        response.data.operationResults.ZPGNDNTZ_DS.features);
                                    grundstueck.bodenbedeckung = this.prepareBoden(grundstueck,
                                        response.data.operationResults.AVGSBBBX_DS.features);
                                    grundstueck.gebaeude =
                                        this.prepareGebaeude(
                                            response.data.operationResults.KGWRGPUB_DS.features,
                                            response.data.operationResults.KGWRWPUB_DS.features,
                                            grundstueck,
                                            response.data.operationResults.AVEINXXX_DS.features,
                                            response.data.operationResults.AVGSBBBX_DS.features);
                                    const projekte: Projekt[] = [];
                                    response.data.operationResults.BAGEDOSS_DS.features.forEach((pro: any) => {
                                        // Baugesuche mit unerwünschtem Verfahrenstyp überspringen
                                        if (service.hideProjekte.includes(pro.attributes.VERFHRTYP)) {
                                            return;
                                        }
                                        const projekt = new Projekt();
                                        projekt.bagenr = pro.attributes.BAGENR;
                                        pro.attributes.LIST_PRZNUMMERN.split(",").forEach(
                                            (nr: string) => {
                                                projekt.prznummern.push(parseInt(nr.trim(), 10));
                                            }
                                        );
                                        if (grundstueck.gbperCode === gbperCode
                                            && projekt.prznummern[0] === grundstueck.nummer) {
                                            projekt.gegenstand = pro.attributes.GEGENSTAND;
                                            projekt.status = pro.attributes.STATUS;
                                            projekt.attributes = pro.attributes;
                                            projekt.geometry = this.addGeometryType(pro.geometry);
                                            projekte.push(projekt);
                                        }
                                    });
                                    grundstueck.projekte = projekte;
                                    this.oStore.addGrundstueck(grundstueck);
                                });
                            }

                            subResolve(nummer);
                        }).catch((error) => {
                            console.error(error);
                            this.oStore.loading = false;
                            this.oStore.status = "error";
                            this.oStore.statusText = service.errorMessage;
                            subResolve(0);
                        });
                    }));
                });
                // Wait until all promises are fulfilled
                Promise.all(promises).then((values) => {
                    this.oStore.loading = false;
                    this.oStore.status = "successful";
                    resolve(values);
                });
            } else {
                resolve(newNummern);
            }
        });
    }
    loadGebaeude(egid: number): Promise<number | null> {
        return new Promise((resolve) => {
            this.oStore.loading = true;
            this.oStore.status = "loading";
            // if (!Array.isArray(egids)){
            //    egids = [egids];
            // }
            const requestOptions = {} as __esri.RequestOptions;
            const service = this.tiConfig;
            requestOptions.responseType = service.xhrOptions.responseType;
            if (service.xhrOptions.usePost) {
                requestOptions.method = "post";
            }
            if (service.xhrOptions.timeout) {
                requestOptions.timeout = service.xhrOptions.timeout;
            }
            const requestData = {
                type: "objektlu",
                perimeter: {
                    selection: {
                        layerName: "KGWRGPUB_DS",
                        filter: " EGID = " + egid + ""
                    }
                }
            };
            requestOptions.query = {
                job: JSON.stringify(requestData),
                f: "pjson"
            };
            esriRequest(service.url, requestOptions).then((response: __esri.RequestResponse) => {
                if (response.data) {
                    response.data.features.forEach((geb: any) => {
                        const gebaeude = new Gebaeude();
                        gebaeude.egid = parseInt(geb.attributes.EGID, 10);
                        gebaeude.egrid = geb.attributes.EGRID;
                        let grundstueck: Grundstueck | undefined;
                        if (gebaeude.egrid) {
                            grundstueck = this.oStore.grundstuecke.get(gebaeude.egrid);
                        }
                        if (!grundstueck && gebaeude.egrid) {
                            this.loadGrundstueck(gebaeude.egrid).then((returnEgrid) => {
                                if (typeof returnEgrid !== "string") {
                                    console.warn("Grundstück nicht gefunden");
                                    this.prepareGebaeude([geb], null, null, null, null);
                                } else {
                                    grundstueck = this.oStore.grundstuecke.get(returnEgrid);
                                    if(!grundstueck ||grundstueck.gebaeude.findIndex((item: Gebaeude) =>
                                        item.egid === gebaeude.egid)<0){
                                        console.warn("Gebäude nicht auf Grundstück");
                                        this.prepareGebaeude([geb], null, null, null, null);
                                    }
                                }
                                this.oStore.loading = false;
                                this.oStore.status = "successful";
                                resolve(egid);
                            });
                        } else {
                            if (grundstueck) {
                                this.prepareGebaeude([geb], null, grundstueck, null, null);
                            } else {
                                this.prepareGebaeude([geb], null, null, null, null);
                            }
                            this.oStore.loading = false;
                            this.oStore.status = "successful";
                            resolve(egid);
                        }
                    });
                } else {
                    this.oStore.loading = false;
                    this.oStore.status = "error";
                    resolve(null);
                }
            }).catch((error) => {
                console.error(error);
                this.oStore.loading = false;
                this.oStore.status = "error";
                this.oStore.statusText = service.errorMessage;
                if (error.details.httpStatus === 400) {
                    this.oStore.status = "not-found";
                    this.oStore.statusText = service.notFoundMessage.replace("{objektArt}", "Gebäude");
                    this.oStore.statusText = this.oStore.statusText.replace("{objektNr}", egid.toString());
                }
                resolve(null);
            });
            return true;
        });
    }
    loadProjekt(bagenr: string): Promise<string | null> {
        return new Promise((resolve) => {
            this.oStore.loading = true;
            this.oStore.status = "loading";
            const requestOptions = {} as __esri.RequestOptions;
            const service = this.tiConfig;
            requestOptions.responseType = service.xhrOptions.responseType;
            if (service.xhrOptions.usePost) {
                requestOptions.method = "post";
            }
            if (service.xhrOptions.timeout) {
                requestOptions.timeout = service.xhrOptions.timeout;
            }
            const requestData = {
                type: "objektlu",
                perimeter: {
                    selection: {
                        layerName: "BAGEDOSS_DS",
                        filter: " BAGENR IN ('" + FormatUtils.encodeForSQL(bagenr) + "')"
                    }
                }
            };
            requestOptions.query = {
                job: JSON.stringify(requestData),
                f: "pjson"
            };
            esriRequest(service.url, requestOptions).then((response: __esri.RequestResponse) => {
                if (response.data) {
                    const projekt = new Projekt();
                    const pro = response.data.features[0];
                    projekt.bagenr = pro.attributes.BAGENR;
                    pro.attributes.LIST_PRZNUMMERN.split(",").forEach(
                        (nr: string) => {
                            projekt.prznummern.push(parseInt(nr.trim(), 10));
                        }
                    );
                    projekt.gegenstand = pro.attributes.GEGENSTAND;
                    projekt.status = pro.attributes.STATUS;
                    projekt.attributes = pro.attributes;
                    projekt.geometry = this.addGeometryType(pro.geometry);
                    projekt.data = this.removeGeometry(response.data);
                    const gbperCode = parseInt(pro.attributes.GBPER_CODE, 10);
                    this.loadGrundstuecke(gbperCode, projekt.prznummern).then(() => {
                        projekt.prznummern.forEach((nummer) => {
                            const egrid = this.oStore.grundstueckMap.get(gbperCode + "-" + nummer);
                            if (egrid) {
                                const grundstueck = this.oStore.grundstuecke.get(egrid);
                                if (grundstueck) {
                                    projekt.grundstuecke.push(grundstueck);
                                }
                            }
                        });
                        this.oStore.addProjekt(projekt);
                        this.oStore.status = "successful";
                        this.oStore.loading = false;
                        resolve(projekt.bagenr);
                    });
                } else {
                    this.oStore.status = "error";
                    this.oStore.loading = false;
                    resolve(null);
                }
            }).catch((error) => {
                console.error(error);
                this.oStore.loading = false;
                this.oStore.status = "error";
                this.oStore.statusText = service.errorMessage;
                if (error.details.httpStatus === 400) {
                    this.oStore.status = "not-found";
                    this.oStore.statusText = service.notFoundMessage.replace("{objektArt}", "Projekt");
                    this.oStore.statusText = this.oStore.statusText.replace("{objektNr}", bagenr);
                }
                resolve(null);
            });
        });
    }
    loadZonenplan(): Promise<void> {
        return new Promise((resolve) =>
        {
            if (this.oStore.zonenplanLoaded) {
                resolve();
            }else if (this.oStore.zonenplanLoading){
                autorun((r)=> {
                    if(this.oStore.zonenplanLoading){
                        resolve();
                        r.dispose();
                    }
                });
            }else{
                this.oStore.setZonenplanLoading(true);
                const zonenLayer =
                    (this.mapStore.map?.layers.find((layer: __esri.MapImageLayer) =>
                        !!layer.url && PathUtils.getMetaId(layer.url) === this.layerlist.zonenplan.metauid
                    ) as __esri.MapImageLayer)
                        .allSublayers.find((l: __esri.Sublayer) => l.id === this.layerlist.zonenplan.layerId);
                zonenLayer.load().then((value: any) => {
                    const values: number[] = [];
                    const promises: Array<Promise<void | HTMLElement>> = [];
                    value.renderer.uniqueValueInfos.forEach((info: __esri.UniqueValueInfo) => {
                        if (typeof info.value === "string") {
                            info.value = parseInt(info.value, 10);
                        }
                        values.push(info.value);
                        this.oStore.zonenplanSymbolMap.set(info.value, info.symbol);
                        const preview = esriSymbolUtils.renderPreviewHTML(info.symbol, {size: 14});
                        promises.push(preview.then((element) => {
                            if (typeof info.value === "string") {
                                info.value = parseInt(info.value, 10);
                            }
                            this.oStore.zonenplanMap.set(info.value, element.innerHTML);
                        }));
                    });
                    this.oStore.zonenplanHelperArray = _.orderBy(values, [], ["desc"]);
                    // Wait until all promises are fulfilled
                    Promise.all(promises).then(() => {
                        this.oStore.setZonenplanLoading(false, true);
                        resolve();
                    });
                });
            }
        });
    }
    loadProjektPreview(): Promise<void> {
        return new Promise((resolve) => {
            if (this.oStore.projektpreviewLoaded) {
                resolve();
            } else if (this.oStore.projektpreviewLoading) {
                autorun((r) => {
                    if (this.oStore.projektpreviewLoading) {
                        resolve();
                        r.dispose();
                    }
                });
            } else {
                this.oStore.setProjektpreviewLoading(true);
                if (this.mapStore.status === "loaded") {
                    return this.loadProjektPreviewLayer();
                } else {
                    const disposer = reaction(() => this.mapStore.status,
                        status => {
                            if (status === "loaded") {
                                disposer();
                                return this.loadProjektPreviewLayer();
                            }
                        });
                }
            }
        });
    }
    private loadProjektPreviewLayer(): Promise<void> {
        return new Promise((resolve, reject) => {
            const projektLayer =
                (this.mapStore.map?.layers.find((layer: __esri.MapImageLayer) =>
                    !!layer.url && PathUtils.getMetaId(layer.url) === this.layerlist.projekt.metauid
                ) as __esri.MapImageLayer)
                    .allSublayers.find((l: __esri.Sublayer) => l.id === this.layerlist.projekt.layerId);
            if (projektLayer) {
                projektLayer.load().then((value: any) => {
                    const promises: Array<Promise<void | HTMLElement>> = [];
                    value.renderer.uniqueValueInfos.forEach((info: __esri.UniqueValueInfo) => {
                        const preview = esriSymbolUtils.renderPreviewHTML(info.symbol);
                        promises.push(preview.then((element) => {
                            if (typeof info.value === "string") {
                                this.oStore.projektpreviewMap.set(info.value, element.innerHTML);
                            }
                        }));
                    });
                    // Wait until all promises are fulfilled
                    Promise.all(promises).then(() => {
                        this.oStore.setProjektpreviewLoading(false, true);
                        resolve();
                    });
                });
            } else {
                console.error("Error: Layer BAGEDOSS_DS (WKOBJEKT_COL-2) nicht gefunden!");
                reject();
            }
        });
    }
    private prepareGrundstueckArt(grundsueck: Grundstueck, grundFeatures: [any]): string {
        let art = "";
        if (grundFeatures.length > 0) {
            const grundFeature = grundFeatures.find((item)=> item.attributes.EGRIS_EGRID === grundsueck.egrid);
            if (grundFeature) {
                art = grundFeature.attributes.ART;
            }
        }
        return art;
    }
    private prepareAdresse(attributes: any): string {
        let adresse = "";
        if(attributes.ORTSCHAFT) {
            if (attributes.STR_NAME) {
                adresse += attributes.STR_NAME + " " + attributes.HAUSNR + "\n";
            }
            if (typeof attributes.PSTCH === "string" && attributes.PSTCH!=="") {
                adresse += "Postfach";
                if(parseInt(attributes.PSTCH, 10)>0){
                    adresse +=  " " + attributes.PSTCH;
                }
                adresse += "\n";
            }
            adresse +=  attributes.PLZ+" "+attributes.ORTSCHAFT;
        }else if(attributes.ADRESSE){
            adresse =  attributes.ADRESSE.replace(/, /g, "\n");
        }
        return adresse;
    }
    private prepareNachfuehrungskreis(grundstueck: Grundstueck, kreisFeatures: [any]): Nachfuehrungskreis {
        const nfkreis = new Nachfuehrungskreis();
        if (kreisFeatures.length > 0) {
            const kreisFeature = kreisFeatures[0];
            nfkreis.id =  parseInt(kreisFeature.attributes.OBJECTID, 10);
            nfkreis.kreis =  kreisFeature.attributes.NF_KREIS;
            nfkreis.nfg =  kreisFeature.attributes.NFG;
            nfkreis.unternehmen =  kreisFeature.attributes.UNTERN;
            nfkreis.adresse = this.prepareAdresse(kreisFeature.attributes);
            nfkreis.tel =  kreisFeature.attributes.CONT_TEL;
            nfkreis.mail =  kreisFeature.attributes.CONT_MAIL;
            nfkreis.webseite =  kreisFeature.attributes.WEBSEITE;
            if (nfkreis.webseite && nfkreis.webseite.indexOf("http")!==0){
                nfkreis.webseite = "https://" + nfkreis.webseite;
            }
            if (kreisFeatures.length > 1) {
                console.warn("Es wurden "+ kreisFeatures.length + " Nachführungskreise gefunden ("
                    + kreisFeatures.reduce(
                        (accu: string, currentValue: any) => accu + (accu? ", ": "") + currentValue.attributes.NF_KREIS
                        , "")
                    + ")");
            }
        }
        return nfkreis;
    }
    private prepareGrundbuchkreis(grundstueck: Grundstueck, kreisFeatures: [any]): Grundbuchkreis {
        const grundbuchkreis = new Grundbuchkreis();
        if (kreisFeatures.length > 0) {
            const kreisFeature = kreisFeatures[0];
            grundbuchkreis.id =  parseInt(kreisFeature.attributes.OBJECTID, 10);
            grundbuchkreis.kreis =  kreisFeature.attributes.GBKREIS;
            grundbuchkreis.geschaeftsstelle =  kreisFeature.attributes.GESCHSTELLE;
            grundbuchkreis.adresse = this.prepareAdresse(kreisFeature.attributes);
            grundbuchkreis.tel =  kreisFeature.attributes.CONT_TEL;
            grundbuchkreis.mail =  kreisFeature.attributes.CONT_MAIL;
            if(kreisFeature.attributes.hasOwnProperty("WEBSEITE")){
                grundbuchkreis.webseite =  kreisFeature.attributes.WEBSEITE;
                if (grundbuchkreis.webseite.indexOf("http")!==0){
                    grundbuchkreis.webseite = "https://" + grundbuchkreis.webseite;
                }
            } else {
                grundbuchkreis.webseite = "https://grundbuch.lu.ch/";
            }
            if (kreisFeatures.length > 1) {
                console.warn("Es wurden "+ kreisFeatures.length + " Grundbuchkreise gefunden ("
                    + kreisFeatures.reduce(
                        (accu: string, currentValue: any) => accu + (accu? ", ": "") + currentValue.attributes.GBKREIS
                        , "")
                    + ")");
            }
        }
        return grundbuchkreis;
    }
    private prepareBauamt(grundstueck: Grundstueck, bauamtFeatures: [any]): Bauamt {
        const bauamt = new Bauamt();
        if (bauamtFeatures.length > 0) {
            const bauamtFeature = bauamtFeatures[0];
            bauamt.id =  parseInt(bauamtFeature.attributes.OBJECTID, 10);
            bauamt.name =  bauamtFeature.attributes.BAUAMT;
            bauamt.adresse = this.prepareAdresse(bauamtFeature.attributes);
            bauamt.tel =  bauamtFeature.attributes.CONT_TEL;
            bauamt.mail =  bauamtFeature.attributes.CONT_MAIL;
            if(bauamtFeature.attributes.hasOwnProperty("WEBSEITE")){
                bauamt.webseite =  bauamtFeature.attributes.WEBSEITE;
                if (bauamt.webseite.indexOf("http")!==0){
                    bauamt.webseite = "https://" + bauamt.webseite;
                }
            } else {
                bauamt.webseite = "";
            }
            if (bauamtFeatures.length > 1) {
                console.warn("Es wurden "+ bauamtFeatures.length + " Bauämter gefunden ("
                    + bauamtFeatures.reduce(
                        (accu: string, currentValue: any) => accu + (accu? ", ": "") + currentValue.attributes.BAUAMT
                        , "")
                    + ")");
            }
        }
        return bauamt;
    }
    private prepareGemeindekanzlei(grundstueck: Grundstueck, gemeindeFeatures: [any]): Gemeindekanzlei {
        const gemeindekanzlei = new Gemeindekanzlei();
        if (gemeindeFeatures.length > 0) {
            const gemeindeFeature = gemeindeFeatures[0];
            gemeindekanzlei.id =  parseInt(gemeindeFeature.attributes.OBJECTID, 10);
            gemeindekanzlei.gemeinde =  gemeindeFeature.attributes.GEMEINDE;
            gemeindekanzlei.name =  gemeindeFeature.attributes.VERW_STELL;
            gemeindekanzlei.name += " " + gemeindeFeature.attributes.VERW_NAME;
            gemeindekanzlei.adresse = this.prepareAdresse(gemeindeFeature.attributes);
            gemeindekanzlei.tel =  gemeindeFeature.attributes.TEL;
            gemeindekanzlei.mail =  gemeindeFeature.attributes.EMAIL;
            if(gemeindeFeature.attributes.hasOwnProperty("WEB")){
                gemeindekanzlei.webseite =  gemeindeFeature.attributes.WEB;
                if (gemeindekanzlei.webseite.indexOf("http")!==0){
                    gemeindekanzlei.webseite = "https://" + gemeindekanzlei.webseite;
                }
            } else {
                gemeindekanzlei.webseite = "";
            }
            if (gemeindeFeatures.length > 1) {
                console.warn("Es wurden "+ gemeindeFeatures.length + " Gemeindekanzleien gefunden ("
                    + gemeindeFeatures.reduce(
                        (accu: string, currentValue: any) => accu + (accu? ", ": "") + currentValue.attributes.GEMEINDE
                        , "")
                    + ")");
            }
        }
        return gemeindekanzlei;
    }
    private prepareZonen(grundstueck: Grundstueck, zonenFeatures: []): Map<number, Zone> {
        const zonen: Map<number, Zone> = new Map();
        zonenFeatures.forEach((item: any) => {
            const type = parseInt(item.attributes.ZONTYP_GDE, 10);
            let zone: Zone | undefined;
            zone = zonen.get(type);
            if (!zone){
                zone = new Zone();
                zone.itemId = "Z-" + type;
                zone.type = type;
                zone.grundnutzung = item.attributes.ZONTYP_KT;
                zone.bezeichnung = item.attributes.ZONTYP_BEZ;
                zone.typeGemeinde = item.attributes.ZONTYP_GDE;
                zone.flaeche = 0;
                zone.anteil = 0;
                zone.geometry = [];
            }
            zone.geometry.push(this.addGeometryType(item.geometry));
            zone.flaeche += parseFloat(item.attributes.SHAPEAREA);
            zone.anteil = 100 / grundstueck.flaeche * zone.flaeche;
            zonen.set(type, zone);
        });
        return zonen;
    }
    private prepareBoden(grundstueck: Grundstueck, bodenFeatures: []): Bodenbedeckung[] {
        const bodenbedeckungArray: Bodenbedeckung[] = [];
        bodenFeatures.forEach((item: any) => {
            if (item.attributes.EGRIS_EGRID === grundstueck.egrid){
                const boden = new Bodenbedeckung();
                boden.id = parseInt(item.attributes.OBJECTID, 10);
                boden.itemId = "B-" + boden.id;
                boden.art = item.attributes.ART;
                boden.gebaeudenummern = item.attributes.GEBAEUDENUMMER;
                boden.flaeche = item.attributes.FLAE_BB;
                boden.geometry = this.addGeometryType(item.geometry);
                bodenbedeckungArray.push(boden);
            }
        });
        return bodenbedeckungArray;
    }
    private prepareGebaeude(gebaeudeFeatures: [any],
                            wohnungFeatures: [] | null,
                            grundstueck: Grundstueck | null,
                            eingangFeatures: [] | null,
                            bodenFeatures: [] | null): Gebaeude[] {
        let gbperCode: number;
        let gbperName = "";
        if (grundstueck) {
            gbperCode = grundstueck.gbperCode;
            gbperName = grundstueck.gbperName;
        }
        const gebaeudeArray: Gebaeude[] = [];
        const eingangMap: Map<number, Eingang[]> = new Map();
        if (eingangFeatures){
            eingangFeatures.forEach((item: any) => {
                const eingang = new Eingang();
                eingang.id = parseInt(item.attributes.OBJECTID, 10);
                eingang.itemId = "E-" + eingang.id;
                eingang.egid = parseInt(item.attributes.GWR_EGID, 10);
                eingang.edid = parseInt(item.attributes.GWR_EDID, 10);
                eingang.strasse = item.attributes.LOK_NAME;
                eingang.nummer = item.attributes.HAUSNUMMER;
                eingang.plz = parseInt(item.attributes.PLZ, 10);
                eingang.zustatzziffern = parseInt(item.attributes.ZUSATZZIFFERN, 10);
                eingang.ort = item.attributes.ORTSCHAFT ;
                eingang.geometry = this.addGeometryType(item.geometry);
                let eingangArray = eingangMap.get(eingang.egid);
                if (!eingangArray) {
                    eingangArray = [];
                }
                eingangArray.push(eingang);
                eingangMap.set(eingang.egid, eingangArray);

            });
        }
        const wohnungMap: Map<number, Wohnung[]> = new Map();
        if (wohnungFeatures){
            wohnungFeatures.forEach((item: any) => {
                const wohnung = new Wohnung();
                wohnung.id = parseInt(item.attributes.OBJECTID, 10);
                wohnung.itemId = "W-" + wohnung.id;
                wohnung.egid = parseInt(item.attributes.EGID, 10);
                wohnung.edid = parseInt(item.attributes.EDID, 10);
                wohnung.ewid = parseInt(item.attributes.EWID, 10);
                wohnung.nummer = wohnung.egid + " / " + wohnung.ewid.toString().padStart(3,"0");
                wohnung.adminNr = item.attributes.WHGNR;
                wohnung.physNr = item.attributes.WEINR;
                wohnung.stockwerk = item.attributes.WSTWK_TXT;
                wohnung.lageStockwerk = item.attributes.WBEZ;
                wohnung.status = item.attributes.WSTAT_TXT;
                wohnung.statusCode = parseInt(item.attributes.WSTAT, 10);
                wohnung.geometry = this.addGeometryType(item.geometry);
                let wohnungArray = wohnungMap.get(wohnung.egid);
                if (!wohnungArray) {
                    wohnungArray = [];
                }
                wohnungArray.push(wohnung);
                wohnungMap.set(wohnung.egid, wohnungArray);

            });
        }
        const bodenMap: Map<string, any> = new Map();
        if (bodenFeatures) {
            bodenFeatures.forEach((item: any) => {
                if (item.attributes.GEBAEUDENUMMER) {
                    item.attributes.GEBAEUDENUMMER.split(",").forEach( (nr: string) => {
                        let boden: any | undefined;
                        nr = nr.trim().toLowerCase();
                        boden = bodenMap.get(nr);
                        if (!boden){
                            boden = {};
                            boden.flaeche = 0;
                            boden.geometry = [];
                        }
                        boden.flaeche += item.attributes.FLAE_BB;
                        boden.geometry.push(this.addGeometryType(item.geometry));
                        bodenMap.set(nr, boden);
                    });
                }
            });
        }
        gebaeudeFeatures.forEach((geb: any) => {
            const gebaeude = new Gebaeude();
            gebaeude.egid = parseInt(geb.attributes.EGID, 10);
            gebaeude.egrid = geb.attributes.EGRID;
            gebaeude.itemId = "G-" + parseInt(geb.attributes.OBJECTID, 10);
            if (parseInt(geb.attributes.GBPER_CODE, 10) > 99) {
                gbperCode = parseInt(geb.attributes.GBPER_CODE, 10);
            }
            // falls der gbperCode weder vom Grundstück noch vom Gebäude kommt, aus der Gebäudenummer extrahieren
            if (!gbperCode) {
                const matches = geb.attributes.GEBNR.match(/^(\d{3})\./);
                if (matches) {
                    gbperCode = parseInt(matches[1], 10);
                }
            }
            let nr = geb.attributes.GEBNR.replace(gbperCode + ".", "");
            if(!isNaN(nr[0])){
                const nrPart = nr.split(/\W/);
                if (nrPart.length === 1){
                    // Nummern wie 472a aufteilen
                    const found = nrPart[0].match(/(\d+)(\D.*)/);
                    if (found && found.length === 3) {
                        nrPart[0] = found[1];
                        nrPart[1] = found[2];
                    }
                }
                if (gbperCode) {
                    gebaeude.nummer = gbperCode + "." + nrPart[0];
                } else {
                    gebaeude.nummer =  "???." + nrPart[0];
                }
                if (nrPart.length > 1){
                    gebaeude.nummer += nrPart[1].toLowerCase();
                }
            }else{
                gebaeude.nummer = nr;
            }

            gebaeude.bezeichnung = geb.attributes.GBEZ;
            gebaeude.status = geb.attributes.GSTAT_TXT;
            gebaeude.gemeinde = geb.attributes.BFS_GEMEINDE;
            gebaeude.gemeindeBfsNr = geb.attributes.BFS_NR;
            gebaeude.gbperCode = gbperCode;
            gebaeude.gbperName = gbperName;
            gebaeude.flaeche = parseInt(geb.attributes.GAREA, 10);
            gebaeude.coordinates = this.addGeometryType(geb.geometry);
            gebaeude.attributes = geb.attributes;
            const eingangArray = eingangMap.get(gebaeude.egid);
            if (eingangArray) {
                gebaeude.eingaenge = eingangArray;
            }
            const wohnungArray = wohnungMap.get(gebaeude.egid);
            if (wohnungArray) {
                gebaeude.wohnungen = wohnungArray;
            }
            nr = nr.replace(".", "").toLowerCase();
            const boden = bodenMap.get(nr);
            if (boden) {
                gebaeude.geometry = boden.geometry;
                if (isNaN(gebaeude.flaeche)){
                    // sollte nicht verwendet werden, da nicht genügend genau.
                    // gebaeude.flaeche = boden.flaeche;
                }
            } else {
                gebaeude.geometry = this.addGeometryType(geb.geometry);
            }
            this.oStore.addGebaeude(gebaeude);
            gebaeudeArray.push(gebaeude);
        });
        return gebaeudeArray;
    }
    private addGeometryType(geometry: any): __esri.Geometry {
        if (geometry.hasOwnProperty("rings")){
            geometry.type = "polygon";
        }else if (geometry.hasOwnProperty("paths")) {
            geometry.type = "polyline";
        }else if (geometry.hasOwnProperty("x") && geometry.hasOwnProperty("y")) {
            geometry.type = "point";
        }
        if (!geometry.spatialReference) {
            geometry.spatialReference = this.config.mapView.spatialReference;
        }
        return geometry;
    }
    private removeGeometry(data: any): any {
        const newData = _.cloneDeep(data);
        newData.features.forEach((feature: any) => {
            if (feature.geometry) {
                if (feature.geometry.hasOwnProperty("rings")){
                    feature.geometry.rings = ["..."];
                }else if (feature.geometry.hasOwnProperty("paths")) {
                    feature.geometry.paths = ["..."];
                }
            }
        });
        if (newData.operationResults){
            for (const key in newData.operationResults) {
                if (newData.operationResults.hasOwnProperty(key)) {
                    newData.operationResults[key].features.forEach((feature: any) => {
                        if (feature.geometry) {
                            if (feature.geometry.hasOwnProperty("rings")) {
                                feature.geometry.rings = ["..."];
                            } else if (feature.geometry.hasOwnProperty("paths")) {
                                feature.geometry.paths = ["..."];
                            }
                        }
                    });
                }
            }
        }

        return newData;
    }
}
